import { useMatomo } from '@datapunt/matomo-tracker-react';
import cx from 'clsx';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import React, { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as FilterIcon } from '@assets/svg/filter.svg';
import { ReactComponent as SearchIcon } from '@assets/svg/search.svg';
import { ReactComponent as StatisticIcon } from '@assets/svg/statistic.svg';

import Onboarding from '@components/Onboarding/Onboarding';
import Header from '@components/common/Header/Header';
import DownloadIconBtn from '@components/common/Header/components/BtnHeaderDownload/BtnHeaderDownload';
import BtnHeaderMap from '@components/common/Header/components/BtnHeaderMap/BtnHeaderMap';
import Maps from '@components/common/Maps/Maps';
import ResetButton from '@components/common/ResetButton/ResetButton';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';
import FilterSearch from '@components/uikit/FilterSearch/FilterSearch';

import { showToast } from '@helpers/showToast';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import useTranslation from '@hooks/useTranslation';

import { useActionItemsGlobalOrIdListQuery } from '@services/actionItem/useActionItemsGlobalOrIdListQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';
import { selectAppPersistOnBoardStart } from '@store/useAppStorePersist';

import ActionItemsButtonAdd from './components/ActionItemsButtonAdd/ActionItemsButtonAdd';
import ActionItemsFilter from './components/ActionItemsFilter/ActionItemsFilter';
import ActionItemsHiddenFilter from './components/ActionItemsHiddenFilter/ActionItemsHiddenFilter';
import ActionItemsSummary from './components/ActionItemsSummary/ActionItemsSummary';
import ActionItemsTableBody from './components/ActionItemsTableBody/ActionItemsTableBody';
import ActionItemsTableBodySkeleton from './components/ActionItemsTableBody/ActionItemsTableBodySkeleton';
import ActionItemsTableHead from './components/ActionItemsTableHead/ActionItemsTableHead';
import StatisticsBlock from './components/StatisticsBlock/StatisticsBlock';
import { getOnboardSteps } from './helpers/getOnboardSteps';
import { useReportActiveActionItemsListMutation } from './services/useReportActiveActionItemsListMutation';
import {
  selectAISort,
  selectActionItemsLastViewed,
  selectActionItemsSearch,
  useActionItemsStoreAction,
} from './store/useActionItemsStore';
import {
  selectAIStatus,
  selectAIType,
  selectAIUrgency,
  selectActionItemsExtraPanel,
  useActionItemsStorePersistAction,
} from './store/useActionItemsStorePersist';
import style from './style.module.scss';

const SORT_AI_TOOLTIP_ID = 'ai-sort-tooltip';

const ActionItems = () => {
  const { t } = useTranslation();
  const { trackPageView } = useMatomo();

  const [showAdditionalFilter, setShowAdditionalFilter] = useState(false);

  const { setAIExtraPanel, setAIResetFilterState } = useActionItemsStorePersistAction();
  const { setActionItemsSearch, setLastViewedAiId } = useActionItemsStoreAction();

  const locationSelectValue = selectAppLocationSelectValue();
  const onBoardStart = selectAppPersistOnBoardStart();
  const extraPanel = selectActionItemsExtraPanel();
  const typeStore = selectAIType();
  const urgencyType = selectAIUrgency();
  const statusType = selectAIStatus();
  const aiListSearch = selectActionItemsSearch();
  const lastViewedAiId = selectActionItemsLastViewed();
  const sorting = selectAISort();

  const [debouncedValue] = useDebouncedValue(aiListSearch, 500);

  const { data: profileData } = useUserProfileQuery();
  const { data: actionItemsData, isLoading } = useActionItemsGlobalOrIdListQuery({
    filter: {
      urgency: urgencyType.value,
      type: typeStore.value,
      state: statusType.value,
    },
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue.filterIds,
    search: debouncedValue,
    enabled: true,
  });
  const { mutate: reportActiveActionItemsListMutate } = useReportActiveActionItemsListMutation();

  useEffect(() => {
    trackPageView({});

    const lastViewedAiElement = document.getElementById(lastViewedAiId);

    if (lastViewedAiElement) {
      lastViewedAiElement.scrollIntoView();
      setLastViewedAiId(null);
    }
  }, []);

  const handleReset = () => {
    setAIResetFilterState();
  };

  const hasAdditionalFilters = typeStore.value !== 'all' || statusType.value !== 'all';
  const hasFilters = urgencyType.value !== 'all' || hasAdditionalFilters;
  const isFilteringActive = hasFilters || !!aiListSearch;
  const actionItemsList = actionItemsData?.data;
  const itemId = actionItemsList?.needResponseList[0]?.id || 'onboarding';
  const steps = getOnboardSteps(t, itemId, onBoardStart);

  const statistic = {
    critical: actionItemsList?.critical || 0,
    high: actionItemsList?.high || 0,
    standard: actionItemsList?.standard || 0,
    total: actionItemsList?.total_action_items || 0,
  };

  const handleChangeExtraPanel = (value: string) => {
    setAIExtraPanel(value);
  };

  const ACTION_ITEMS_TEXT_TOOLTIP_ID = 'action-items-text-tooltip';
  const ADDITIONAL_FILTER_TOOLTIP_ID = 'additional-filter-tooltip';

  const exportActionItemsList = () => {
    const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;
    showToast({ variant: 'success', message: t('The download will begin shortly.') });

    reportActiveActionItemsListMutate(
      {
        filter: {
          urgency: urgencyType.value,
          type: typeStore.value,
          state: statusType.value,
        },
        locationId: locationSelectValue.id,
        facilityId: locationSelectValue.facility_id,
        levelName: locationSelectValue.level_name,
        searchValue: debouncedValue,
        sorting,
      },
      {
        onSuccess: (data) => {
          saveAs(
            new Blob([data?.data], { type: 'text/plain;charset=utf-8' }),
            `Active Action Items List ${format(new Date(), currentDateFormat)}.xlsx`,
          );
        },
      },
    );
  };

  return (
    <div>
      <Header
        title={t('Action items list')}
        showLogo={true}
        showFacilitySelect={true}
        headerLeftComponent={<BtnHeaderMap />}
        headerRightComponent={
          <DownloadIconBtn
            tooltipInfo={t('Download Action Items Report')}
            downloadFn={exportActionItemsList}
          />
        }
      />

      <main className={style.actionItemsPage}>
        <Maps />
        <StatisticsBlock />
        <div className={style.actionItemsPanelContainer}>
          <div className={style.actionItemsPanel}>
            <div className={style.actionItemsPanelFilter}>
              <ActionItemsFilter />

              <div
                className={style.additionalFiltersMenu}
                onClick={() => setShowAdditionalFilter(true)}
                data-tooltip-id={ADDITIONAL_FILTER_TOOLTIP_ID}
              >
                <div
                  className={cx(style.iconContainer, {
                    [style.active]: showAdditionalFilter,
                  })}
                  data-tooltip-content={t('Show Filters')}
                  data-tooltip-id={ACTION_ITEMS_TEXT_TOOLTIP_ID}
                >
                  <span
                    className={cx({
                      [style.activeExtraFiltersIconDot]: hasAdditionalFilters,
                    })}
                  >
                    <FilterIcon className={style.filterIcon} />
                  </span>
                </div>
              </div>
              {hasFilters && (
                <div className={style.actionItemsPanelItemReset}>
                  <ResetButton showResetButton={hasFilters} onClick={handleReset} />
                </div>
              )}
            </div>

            <div className={style.actionItemsPanelBtns}>
              {profileData?.userAccess?.manageData && <ActionItemsButtonAdd />}
              <div className={style.switcherBlock}>
                <div
                  className={cx(style.switchItem, {
                    [style.switchItemActive]: extraPanel === 'statistic' || onBoardStart,
                  })}
                  onClick={() => handleChangeExtraPanel('statistic')}
                  data-tooltip-content={t('General Statistics Panel')}
                  data-tooltip-id={ACTION_ITEMS_TEXT_TOOLTIP_ID}
                >
                  <StatisticIcon />
                </div>
                <div
                  className={cx(style.switchItem, { [style.switchItemActive]: extraPanel === 'search' })}
                  onClick={() => handleChangeExtraPanel('search')}
                  data-tooltip-content={t('Search Bar')}
                  data-tooltip-id={ACTION_ITEMS_TEXT_TOOLTIP_ID}
                >
                  <SearchIcon className={style.searchIcon} />
                </div>
              </div>
            </div>
          </div>
          {(extraPanel === 'statistic' || onBoardStart) && <ActionItemsSummary statistic={statistic} />}
          {extraPanel === 'search' && (
            <div className={style.searchBlock}>
              <FilterSearch
                value={aiListSearch}
                onChange={setActionItemsSearch}
                variant={'header-search'}
                placeholder={t('Start to type...')}
              />
            </div>
          )}
        </div>

        <div className={style.actionItemsTable}>
          {!isLoading && actionItemsList ? (
            <>
              <ActionItemsTableHead
                hasWorkOrder={actionItemsList.hasWorkOrder}
                sortTooltipId={SORT_AI_TOOLTIP_ID}
              />
              <ActionItemsTableBody
                tourId="tourStep-7"
                title={t('Need response')}
                hasWorkOrder={actionItemsList.hasWorkOrder}
                items={actionItemsList.needResponseList}
                tooltipText={t('Action Items that need a response from on-site personnel')}
                isFilteringActive={isFilteringActive}
              />
              <ActionItemsTableBody
                tourId="tourStep-8"
                title={t('PENDING UPDATE')}
                hasWorkOrder={actionItemsList.hasWorkOrder}
                items={actionItemsList.waitingList}
                tooltipText={t(
                  'Action Items that do not currently require a response from on-site personnel (can include items that are snoozed, on hold, or waiting for a response from Waites)',
                )}
                isFilteringActive={isFilteringActive}
              />
              <Onboarding steps={steps} />
            </>
          ) : (
            <>
              <ActionItemsTableHead hasWorkOrder />
              <ActionItemsTableBodySkeleton />
            </>
          )}
        </div>
      </main>
      <Tooltip id={ACTION_ITEMS_TEXT_TOOLTIP_ID} className={style.textTooltip} />
      <ActionItemsHiddenFilter
        id={ADDITIONAL_FILTER_TOOLTIP_ID}
        visible={showAdditionalFilter}
        setIsOpen={setShowAdditionalFilter}
      />
      <TooltipSort id={SORT_AI_TOOLTIP_ID} sorting={sorting} />
    </div>
  );
};

export default ActionItems;
